import { GEO_CONDITIONS } from "./filterTypes";
import type {
  FilterConditionValue,
  GeoDisplayUnit,
  GeoFilterCondition,
} from "./filterTypes";
import type { NominatimReverseResult } from "@/services/geocodingService";

/** data_type the backend `/filters/` metadata uses for geo fields. */
export const GEO_DATA_TYPE = "geo";

/** Field name of the geo filter per the backend contract. */
export const DEFAULT_GEO_FILTER_FIELD = "location";

export const KM_PER_MILE = 1.609344;

export const DEFAULT_RADIUS_MILES = 50;

export const DEFAULT_GEO_DISPLAY_UNIT: GeoDisplayUnit = "mi";

export const isGeoFilterCondition = (
  condition: unknown,
): condition is GeoFilterCondition => {
  return (
    typeof condition === "object" &&
    condition !== null &&
    "condition" in condition &&
    GEO_CONDITIONS.includes((condition as GeoFilterCondition).condition)
  );
};

export const isGeoFilter = (
  filterValue: FilterConditionValue | undefined,
): filterValue is GeoFilterCondition[] => {
  return (
    Array.isArray(filterValue) &&
    filterValue.length > 0 &&
    isGeoFilterCondition(filterValue[0])
  );
};

export const createGeoFilter = ({
  lat,
  lng,
  radiusKm,
  displayUnit = DEFAULT_GEO_DISPLAY_UNIT,
  label,
}: {
  lat: number;
  lng: number;
  radiusKm: number;
  displayUnit?: GeoDisplayUnit;
  label?: string | null;
}): GeoFilterCondition => ({
  condition: "within_radius",
  lat,
  lng,
  radius_km: radiusKm,
  display_unit: displayUnit,
  ...(label !== undefined && { label }),
});

const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10;

export const kmToDisplayUnit = (radiusKm: number, unit: GeoDisplayUnit) =>
  roundToOneDecimal(unit === "mi" ? radiusKm / KM_PER_MILE : radiusKm);

export const displayUnitToKm = (value: number, unit: GeoDisplayUnit) =>
  unit === "mi" ? value * KM_PER_MILE : value;

export const formatCoordinateLabel = (lat: number, lng: number) =>
  `${lat.toFixed(3)}, ${lng.toFixed(3)}`;

/**
 * Builds a short place label ("Boston, MA" / "Berlin, Germany") from a
 * Nominatim reverse-geocode result. Returns null when no usable place
 * component is present so callers can fall back to raw coordinates.
 */
export const formatPlaceLabel = (
  result: NominatimReverseResult | null,
): string | null => {
  const address = result?.address;
  if (!address) return null;

  const place =
    address.city ||
    address.town ||
    address.village ||
    address.municipality ||
    address.county ||
    address.state;
  if (!place) return address.country ?? null;

  // ISO3166-2-lvl4 is "US-MA" — the part after the dash is the state code.
  const stateCode = address["ISO3166-2-lvl4"]?.split("-")[1];
  const isUnitedStates = address.country_code?.toLowerCase() === "us";
  const region =
    isUnitedStates && stateCode ? stateCode : (address.country ?? null);

  if (!region || region === place) return place;
  return `${place}, ${region}`;
};

const formatRadius = (radius: number) =>
  Number.isInteger(radius) ? String(radius) : radius.toFixed(1);

/** Chip/tile text, e.g. `Within 50 mi of Boston, MA`. */
export const formatGeoFilterDisplayText = (
  condition: GeoFilterCondition,
): string => {
  const unit = condition.display_unit ?? DEFAULT_GEO_DISPLAY_UNIT;
  const radius = formatRadius(kmToDisplayUnit(condition.radius_km, unit));
  const place =
    condition.label || formatCoordinateLabel(condition.lat, condition.lng);
  return `Within ${radius} ${unit} of ${place}`;
};

/**
 * Resolves the geo filter field for a dataset from its `/filters/` metadata,
 * falling back to the contract default while the backend metadata support is
 * still rolling out.
 */
export const getGeoFilterField = (
  allFiltersData: Record<string, { data_type?: string }> | undefined,
): string => {
  const geoEntry = Object.entries(allFiltersData ?? {}).find(
    ([, meta]) => meta?.data_type === GEO_DATA_TYPE,
  );
  return geoEntry?.[0] ?? DEFAULT_GEO_FILTER_FIELD;
};

/**
 * True when the dataset's `/filters/` metadata advertises a geo field —
 * i.e. the backend can evaluate `within_radius`. The radius tool stays
 * hidden until then so deployed environments never emit a filter the
 * backend would reject.
 */
export const hasGeoFilterSupport = (
  allFiltersData: Record<string, { data_type?: string }> | undefined,
): boolean =>
  Object.values(allFiltersData ?? {}).some(
    (meta) => meta?.data_type === GEO_DATA_TYPE,
  );
